import { memo, useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PH, Av } from '../components/shared';
import { useApp } from '../context/AppContext';

const RAW_API = (process.env.REACT_APP_API_BASE || '').replace(/\/+$/, '');
const API = /\/api$/.test(RAW_API) ? RAW_API : `${RAW_API}/api`;
const authHeaders = () => ({
  'Content-Type':'application/json',
  'Accept':'application/json',
  Authorization: `Bearer ${localStorage.getItem('gotap_token') || ''}`,
});

export const PageNotificationCompose = memo(() => {
  const navigate = useNavigate();
  const { user, showToast } = useApp();

  const [members, setMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(true);
  const [query, setQuery]       = useState('');
  const [selected, setSelected] = useState(new Set());
  const [title, setTitle]       = useState('');
  const [body, setBody]         = useState('');
  const [action, setAction]     = useState('');
  const [priority, setPriority] = useState('normal');
  const [sending, setSending]   = useState(false);

  // Fetch team members. Backend returns 403 if the caller isn't enterpriser/admin,
  // and we surface that as "No team members found" below.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/notifications/team-members`, { headers: authHeaders() });
        if (!res.ok) {
          setMembers([]);
          return;
        }
        const data = await res.json();
        setMembers(data.members || []);
      } catch {
        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    })();
  }, []);

  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members;
    return members.filter(m =>
      (m.name||'').toLowerCase().includes(q) ||
      (m.email||'').toLowerCase().includes(q));
  }, [members, query]);

  const toggle = (id) => setSelected(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  // Select-all is additive: clicking it when all visible are already selected
  // removes only the visible ones; otherwise it adds the visible ones to the
  // existing selection (preserving picks from prior searches).
  const allVisibleSelected = filteredMembers.length > 0 && filteredMembers.every(m => selected.has(m.id));
  const toggleAll = () => setSelected(prev => {
    const next = new Set(prev);
    if (allVisibleSelected) filteredMembers.forEach(m => next.delete(m.id));
    else                    filteredMembers.forEach(m => next.add(m.id));
    return next;
  });

  const canSend = selected.size > 0 && title.trim() && body.trim() && !sending;

  const onSend = async () => {
    if (!canSend) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/notifications/team-send`, {
        method:'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          recipient_ids: [...selected],
          title: title.trim(),
          body:  body.trim(),
          action: action.trim() || null,
          priority,
        }),
      });
      if (!res.ok) throw new Error((await res.json())?.message || 'Send failed');
      const data = await res.json();
      showToast(`✅ Sent to ${data.sent} team member${data.sent === 1 ? '' : 's'}`);
      setSelected(new Set()); setTitle(''); setBody(''); setAction(''); setPriority('normal');
    } catch (e) {
      showToast(`❌ ${e.message}`, 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="pg fi">
      <PH
        eyebrow="Notifications"
        title="Send Team Notification"
        sub="Pick recipients, write a message — they'll receive it in real time."
        action={<button className="btn" onClick={()=>navigate('/notifications')}>Back to Notifications</button>}
      />

      <div className="g2" style={{alignItems:"start"}}>

        {/* ── Recipients panel ── */}
        <div className="card-s" style={{padding:18,display:"flex",flexDirection:"column",gap:12,maxHeight:"calc(100vh - 240px)"}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between"}}>
            <div>
              <div style={{fontSize:15,fontWeight:700,fontFamily:"var(--fd)"}}>Recipients</div>
              <div style={{fontSize:12,color:"var(--t3)"}}>{selected.size} of {members.length} selected</div>
            </div>
            <button onClick={toggleAll} className="btn" disabled={filteredMembers.length === 0}
              style={{padding:"6px 12px",fontSize:12,opacity: filteredMembers.length === 0 ? .5 : 1}}>
              {allVisibleSelected ? `Unselect ${filteredMembers.length} visible` : `Select all ${query ? 'matching' : ''}`}
            </button>
          </div>
          <input className="inp" placeholder="Search team members…"
                 value={query} onChange={e=>setQuery(e.target.value)} />

          <div style={{flex:1,overflowY:"auto",display:"flex",flexDirection:"column",gap:8,paddingRight:4}}>
            {loadingMembers && <div style={{padding:20,textAlign:"center",color:"var(--t3)",fontSize:13}}>Loading team members…</div>}
            {!loadingMembers && filteredMembers.length === 0 && (
              <div style={{padding:30,textAlign:"center",color:"var(--t3)",fontSize:13}}>
                No team members found.
              </div>
            )}
            {filteredMembers.map(m => {
              const picked = selected.has(m.id);
              return (
                <div key={m.id} onClick={()=>toggle(m.id)}
                  style={{display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:12,
                          border:"1px solid rgba(0,0,0,.06)",cursor:"pointer",
                          background: picked ? "rgba(59,130,246,.08)" : "rgba(255,255,255,.6)",
                          transition:"background .15s"}}>
                  <div style={{width:20,height:20,borderRadius:6,border: picked ? "none" : "1.5px solid rgba(0,0,0,.15)",
                               background: picked ? "var(--blue)" : "transparent",
                               display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12,fontWeight:700}}>
                    {picked ? "✓" : ""}
                  </div>
                  <Av name={(m.name||"??").slice(0,2).toUpperCase()} size={34}
                      grad="linear-gradient(150deg,#64748b,#3b82f6)" />
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13.5,fontWeight:600,color:"var(--t1)"}}>{m.name}</div>
                    <div style={{fontSize:11.5,color:"var(--t3)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                      {m.job_title ? `${m.job_title} · ` : ''}{m.email}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Composer + preview ── */}
        <div style={{display:"flex",flexDirection:"column",gap:14}}>
          <div className="card-s" style={{padding:18,display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontSize:15,fontWeight:700,fontFamily:"var(--fd)"}}>Message</div>

            <div>
              <label style={{fontSize:12,fontWeight:600,color:"var(--t2)",marginBottom:4,display:"block"}}>Title *</label>
              <input className="inp" value={title} maxLength={80} onChange={e=>setTitle(e.target.value)}
                     placeholder="Short headline" />
            </div>
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"var(--t2)",marginBottom:4,display:"block"}}>Body *</label>
              <textarea className="inp" rows={4} value={body} maxLength={240}
                        onChange={e=>setBody(e.target.value)} placeholder="Message body…"
                        style={{resize:"vertical",minHeight:80}} />
              <div style={{fontSize:11,color:"var(--t4)",textAlign:"right",marginTop:2}}>{body.length}/240</div>
            </div>
            <div>
              <label style={{fontSize:12,fontWeight:600,color:"var(--t2)",marginBottom:4,display:"block"}}>
                Action link <span style={{fontWeight:400,color:"var(--t4)"}}>(optional)</span>
              </label>
              <input className="inp" value={action} onChange={e=>setAction(e.target.value)}
                     placeholder="/leads, /team, or full URL" />
            </div>

            <div>
              <label style={{fontSize:12,fontWeight:600,color:"var(--t2)",marginBottom:6,display:"block"}}>Priority</label>
              <div style={{display:"flex",gap:8}}>
                {[
                  {key:"normal", label:"● Normal", hint:"Firestore only"},
                  {key:"high",   label:"● High",   hint:"Also push + sound"},
                ].map(p => (
                  <button key={p.key} onClick={()=>setPriority(p.key)}
                    style={{padding:"8px 16px",borderRadius:999,border:"1px solid rgba(0,0,0,.08)",cursor:"pointer",
                            fontSize:12.5,fontWeight:600,
                            background: priority===p.key ? (p.key==='high'?"var(--red)":"var(--t1)") : "transparent",
                            color:     priority===p.key ? "#fff" : "var(--t2)"}}>
                    {p.label}
                    <span style={{fontSize:10,fontWeight:400,marginLeft:6,opacity:.8}}>{p.hint}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Live preview */}
          <div className="card-s" style={{padding:18}}>
            <div style={{fontSize:12,fontWeight:600,color:"var(--t3)",marginBottom:10,textTransform:"uppercase",letterSpacing:.5}}>Preview</div>
            <div style={{display:"flex",gap:12,padding:"12px 14px",borderRadius:14,
                         background:"rgba(59,130,246,.06)",border:"1px solid rgba(0,0,0,.05)"}}>
              <div style={{width:40,height:40,borderRadius:12,background:"rgba(16,185,129,.18)",
                           display:"flex",alignItems:"center",justifyContent:"center",fontSize:18}}>👥</div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{display:"flex",alignItems:"baseline",justifyContent:"space-between"}}>
                  <div style={{fontSize:13.5,fontWeight:700,color:"var(--t1)"}}>
                    {priority==='high' && <span style={{display:"inline-block",width:6,height:6,borderRadius:"50%",background:"var(--red)",marginRight:6}}/>}
                    {title || "Your title will appear here"}
                  </div>
                  <div style={{fontSize:11,color:"var(--t4)"}}>now</div>
                </div>
                <div style={{fontSize:12.5,color:"var(--t3)",lineHeight:1.5,marginTop:3,marginBottom:8}}>
                  {body || "Your message body will appear here."}
                </div>
                <span style={{display:"inline-block",padding:"3px 10px",fontSize:11,fontWeight:600,
                             color:"var(--green)",background:"rgba(16,185,129,.12)",borderRadius:999}}>Team</span>
                {action && <div style={{fontSize:11,color:"var(--blue)",marginTop:6}}>↪ {action}</div>}
              </div>
            </div>
          </div>

          <button onClick={onSend} disabled={!canSend}
            style={{padding:"14px 20px",fontSize:14,fontWeight:700,borderRadius:14,cursor: canSend ? "pointer":"not-allowed",
                    border:"none",background: canSend ? "var(--t1)" : "rgba(100,116,139,.25)",color:"#fff",
                    boxShadow: canSend ? "0 6px 20px rgba(15,23,42,.25)" : "none"}}>
            {sending ? "Sending…" : `Send to ${selected.size} team ${selected.size === 1 ? 'member' : 'members'}`}
          </button>
        </div>
      </div>
    </div>
  );
});
